import { Component, inject, OnDestroy } from '@angular/core';
import { StoredStateService } from 'src/app/core/service/stored-state.service';
import { StoredState, StoredContext, StoredData } from '../../core/model/api';
import { RadioButtonComponent } from "../controls/radio-button/radio-button.component";
import { Subject, map, merge } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { SwitcherComponent } from "../controls/switcher/switcher.component";
import { SeparatorComponent } from "../separator/separator.component";
import { ThemeService } from 'src/app/core/service/theme.service';
import { Theme } from 'src/app/core/model/common';
import { DealSoundService } from 'src/app/core/service/deal-sound.service';
import { AppTranslateService } from '../../core/service/translate.service';
import { TranslateModule } from '@ngx-translate/core';

interface Settings {
  'app.theme': 'light' | 'dark'
  'app.lang': string
  'terminal.dealsSound': boolean
}

@Component({
  selector: 'app-settings',
  imports: [RadioButtonComponent, SwitcherComponent, SeparatorComponent, TranslateModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnDestroy {

  private stateService = inject(StoredStateService)
  private themeService = inject(ThemeService)
  private dealSoundService = inject(DealSoundService)
  private translate = inject(AppTranslateService)
  private defaultSettings: Settings = {
    'app.theme': 'light',
    'app.lang': 'ru',
    'terminal.dealsSound': true
  }

  protected settings?: Settings

  private changedSettings$ = new Subject<Partial<Settings>>()

  private sb = merge(
    this.stateService.getState(this.prepareRequestData()).pipe(
      map(states => this.fillSettings(this.defaultSettings, states || []))
    ),
    this.changedSettings$.pipe(
      map(changedSettings => this.settings ? this.mergeSettings(this.settings, changedSettings) : this.defaultSettings)
    )
  ).subscribe(settings => this.settings = settings)

  ngOnDestroy() {
    this.sb.unsubscribe()
    this.changedSettings$.complete()
  }

  protected save(name: string, data: any) {
    if (name === 'app.theme') {
      this.themeService.setTheme(data as Theme)
    } else if (name === 'app.lang') {
      this.translate.switchLanguage(data as string)
    } else if (name === 'terminal.dealsSound') {
      this.dealSoundService.setFlag(data as boolean)
    } else {
      this.stateService.setState([{name, data, context: 'global'}]).subscribe()
    }
    this.changedSettings$.next({[name]: data})
  }

  private prepareRequestData() {
    return Object.keys(this.defaultSettings).map(name => ({name, context: 'global'}))
  }

  private fillSettings(defaultSettings: Settings, states: StoredState<StoredContext, StoredData>[]) {
    const data = cloneDeep(defaultSettings)
    states.forEach(state => {
      const name = state.name as keyof Settings
      if (name === 'app.theme') {
        data[name] = state.data as Settings['app.theme']
      } else if (name === 'app.lang') {
        data[name] = state.data as Settings['app.lang']
      } else if (name === 'terminal.dealsSound') {
        data[name] = state.data as Settings['terminal.dealsSound']
      }
    })
    return data
  }

  private mergeSettings(defaultSettings: Settings, changedSettings: Partial<Settings>) {
    return {
      ...defaultSettings,
      ...changedSettings
    }
  }

}
